import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Loader from "~/app/common/Loader";
import { route } from '~/app/helpers/route';
import usePageTitle from '~/app/hooks/usePageTitle';
import { useAppHeader } from '~/app/modules/appHeader';
import { ROUTES } from '~/app/Router';
import Service from "~/services/Details/Service";
import { useStoreActions, useStoreState } from '~/store';
import { ServiceDetails } from "~/types";
import ServiceActions from './ServiceActions';
import classes from './style.module.scss';
import WorkOrder from '~/issueService/WorkOrder';
import StatusMessage from './statusMessage';
import AccessControl from '~/accessControl';
import { PERMISSION_GROUPS } from '~/accessControl/permissionGroups';
import Messages from '~/messages';
import { MessageType, IssueOrServiceType } from '~/app/data/statuses/chat';
import ServiceChecklistSection from './ServiceChecklistSection';
import { ServiceStatuses } from '~/app/data/statuses/services';
import CloseServiceDetails from './ClosedServiceDetails';
import Parts from '~/parts';
import Materials from '~/materials';
import { PartLinkTypes } from '~/app/data/statuses/issue';

export type ServiceDetailsComponent = React.FC;

const ServiceDetail: ServiceDetailsComponent = () => {
    const [fetching, setFetching] = useState<boolean>(true);
    const { serviceId } = useParams<{ serviceId: ServiceDetails['id'] }>();

    const serviceDetail = useStoreState(state => state.service.data);
    const ServiceFetchAction = useStoreActions(actions => actions.service.fetchService);

    const history = useHistory();
    const { setData } = useAppHeader();

    useEffect(() => {
        ServiceFetchAction(serviceId).then(() => setFetching(false));
    }, [serviceId, setFetching, ServiceFetchAction]);

    const pageTitle = useMemo(() => {
        if (!serviceDetail) return '';
        return `${serviceDetail.serviceText}`
    }, [serviceDetail]);

    usePageTitle(pageTitle);
    useEffect(() => {
        if (!serviceDetail) return;
        setData({
            title: pageTitle,
            subTitle: (serviceDetail.machineIntern + " - " + serviceDetail.machineName).toUpperCase(),
            backLink: history.length > 1 ? history.goBack : route(ROUTES.projects),
            showUser: false,
            machineId: '',
            projectId: '',
            showMachineMessages: false,
            isIssueDetailPage: false,
            isServiceDetailPage: true,
            notification: false,
            search: false
        });
    }, [setData, serviceDetail, pageTitle, history]);

    if (fetching) {
        return <Loader size="32" />;
    }

    return (
        <div className={classes['service-container']}>
            <StatusMessage item={serviceDetail} />
            <Service service={serviceDetail} />
            {serviceDetail.workOrderNo && <WorkOrder type={IssueOrServiceType.SERVICE} {...serviceDetail} />}
            {serviceDetail.status === ServiceStatuses.CANCELLED && <CloseServiceDetails service={serviceDetail} />}
            <ServiceChecklistSection />
            <Parts type={PartLinkTypes.SERVICE} />
            <Materials type={PartLinkTypes.SERVICE} />
            <Messages issue={serviceDetail} type={MessageType.SERVICE}/>
            <AccessControl requiredPermissions={PERMISSION_GROUPS.writeServiceDetails}>
                <div style={{ marginTop: 'auto' }}>
                    <ServiceActions />
                </div>
            </AccessControl>
        </div>
    );
};

export default ServiceDetail;
